import { t } from '@lingui/core/macro'

import type { WidgetState } from './Copilot'
import type { EventState } from './usePuzzelEvents/usePuzzelEvents'
import type { SettingsResponse } from './useSettings'

const disabledReason = ({
  answered,
  currentQueue,
  isWhitelistedQueue,
  hasTranscription,
}: {
  currentQueue: string
  isWhitelistedQueue: boolean
  answered: boolean
  hasTranscription: boolean
}): string => {
  if (!answered) {
    return t`You need to answer the call before you can create a live summary`
  }
  if (!isWhitelistedQueue) {
    return t`Live summary is not enabled for ${currentQueue}`
  }
  if (!hasTranscription) {
    return t`There is no transcription for this conversation yet`
  }

  return ''
}

const getLiveSummaryState = ({
  enabled,
  answered,
  currentQueue,
  isWhitelistedQueue,
  hasTranscription,
  lockSummaryEdit,
}: {
  currentQueue: string
  isWhitelistedQueue: boolean
  answered: boolean
  hasTranscription: boolean
  enabled: boolean
  lockSummaryEdit: boolean
}): WidgetState['modules']['liveSummary'] => {
  if (enabled) {
    const liveSummaryDisabled = !(
      isWhitelistedQueue &&
      answered &&
      hasTranscription
    )

    if (liveSummaryDisabled) {
      return {
        enabled: true,
        buttonDisabled: true,
        lockSummaryEdit,
        reason: disabledReason({
          answered,
          currentQueue,
          isWhitelistedQueue,
          hasTranscription,
        }),
      }
    }

    return {
      enabled: true,
      buttonDisabled: false,
      lockSummaryEdit,
      reason: '',
    }
  }

  return { enabled: false }
}

export const createState = ({
  eventState: { currentCallState, data },
  search,
  settings,
}: {
  eventState: EventState
  settings?: SettingsResponse
  search: string
}): WidgetState => {
  const params = new URLSearchParams(search)

  const isWhitelistedQueue =
    settings?.queues.has(data.queue.toLowerCase()) || false
  const isLockedSummaryQueue =
    settings?.lockedQueues.has(data.queue.toLowerCase()) || false

  const answered =
    currentCallState === 'handling' || currentCallState === 'wrapUp'

  const liveSummary = getLiveSummaryState({
    answered,
    currentQueue: data.queue,
    hasTranscription: data.hasTranscription,
    isWhitelistedQueue,
    enabled: !!settings?.modules.liveSummary,
    lockSummaryEdit: isLockedSummaryQueue,
  })
  return {
    data,
    currentCallState,
    modules: {
      askCopilot: { enabled: settings?.modules.askCopilot || false },
      handover: settings?.modules.handover
        ? { enabled: true, show: true }
        : { enabled: false },
      voiceBotHandover: settings?.modules.voiceBotHandover
        ? { enabled: true, show: true }
        : { enabled: false },
      liveSummary,
      previousInteractions: {
        enabled: settings?.modules.previousInteractions || false,
      },
    },
    config: {
      waitUntilAnswer: false,
      legacyApiToken: params.get('token') || 'token_missing_from_search_params',
      copilotApiToken:
        params.get('copilotToken') || 'token_missing_from_search_params',
    },
  }
}
